import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { formatGeolocationCoordinates } from '../../composite-subfields.util';
import { ReadonlyDisplayBase } from './readonly-display.base';
import { ValueDisplayComponent } from './value-display.component';

/**
 * Read display for a `geolocation` answer (#21239): `latitude, longitude` exactly as stored — no
 * rounding, no reprojection, no degree sign, no map (YAGNI, the task only asks for coordinates).
 * A lone coordinate (the other side missing/empty) renders on its own — `52.37` — rather than
 * being treated as no answer; only a value with NEITHER side falls back to plain text.
 *
 * The coordinate text itself is `formatGeolocationCoordinates` (`composite-subfields.util.ts`) —
 * the SAME function `formatGeolocationValue` (`review-value.util.ts`) calls for the two surfaces
 * that have no display registry of their own (wizard-review, readonly-onboarding both hand a
 * geolocation composite's raw value straight to `formatDisplayValue`, fix round 2). One function,
 * so this row and those two surfaces cannot drift on the separator or on the lone-coordinate rule.
 *
 * `geolocation.component.ts` patches a HIDDEN `geolocation` sub-control with
 * `JSON.stringify({ latitude, longitude, markerColor })` on every change.
 * `formatGeolocationCoordinates` only ever reads `latitude`/`longitude` off the value, so that
 * hidden sub-control cannot reach the coordinate text by construction. `latitude`/`longitude`/
 * `geolocation` are also in `TECHNICAL_SUBKEYS` (`review-value.util.ts`, fix round 1), so it
 * cannot leak through the `<dd-value-display>` fallback below either — `fallbackValue` strips it a
 * second time regardless, so that stays true even if `TECHNICAL_SUBKEYS` ever lost the entry.
 *
 * Presentational: both inputs come from the row.
 */
@Component({
  selector: 'dd-geolocation-display',
  imports: [ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — the display must not become a box inside the value span, or the row's
  // truncate/multiline behaviour would stop at this element.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    @if (coordinates(); as coordinates) {
      {{ coordinates }}
    } @else {
      <dd-value-display [field]="field()" [value]="fallbackValue()" />
    }
  `,
})
export class GeolocationDisplayComponent extends ReadonlyDisplayBase {
  /**
   * `latitude, longitude`, a lone coordinate, or `null` when neither side is present — only
   * `null` falls back to `<dd-value-display>`.
   */
  readonly coordinates = computed(() => formatGeolocationCoordinates(this.value()));

  /**
   * Only reached when `coordinates()` is `null` (neither side present): the stored value with the
   * hidden `geolocation` sub-control left out, so the fallback text row can never print the raw
   * JSON either — see the class doc.
   */
  readonly fallbackValue = computed(() => {
    const raw = this.value();
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return raw;
    }
    const { latitude, longitude } = raw as Record<string, unknown>;
    return { latitude, longitude };
  });
}
